import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const RadarContainer = styled.section`
  height: 100vh;
  position: relative;
  background: linear-gradient(180deg, #000000 0%, #0a0a0a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const RadarContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
`;

const RadarTitle = styled(motion.h2)`
  font-family: 'Orbitron', monospace;
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 700;
  color: #00ffff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const RadarSubtitle = styled(motion.p)`
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  color: #cccccc;
  margin-bottom: 2rem;
`;

const ThreatStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2rem;
`;

const StatItem = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: 'Orbitron', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #ff0000;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #888888;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const GlobeContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Globe = styled(motion.div)`
  width: 300px;
  height: 300px;
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  position: relative;
  background: radial-gradient(circle, rgba(0, 17, 34, 0.8) 0%, rgba(0, 0, 0, 0.9) 100%);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 280px;
    height: 280px;
    border: 1px solid rgba(0, 255, 255, 0.2);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 260px;
    height: 260px;
    border: 1px solid rgba(0, 255, 255, 0.1);
    border-radius: 50%;
  }
`;

const RadarRings = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  pointer-events: none;
`;

const RadarRing = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  
  &:nth-child(1) { width: 200px; height: 200px; }
  &:nth-child(2) { width: 400px; height: 400px; }
  &:nth-child(3) { width: 600px; height: 600px; }
`;

const ThreatDot = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: #ff0000;
  border-radius: 50%;
  box-shadow: 0 0 10px #ff0000;
`;

const ThreatRadar = () => {
  const [threatCount, setThreatCount] = React.useState(0);
  const [activeThreats, setActiveThreats] = React.useState(0);
  const [blockedAttacks, setBlockedAttacks] = React.useState(0);

  useEffect(() => {
    const updateStats = () => {
      setThreatCount(prev => prev + Math.floor(Math.random() * 3));
      setActiveThreats(Math.floor(Math.random() * 15) + 5);
      setBlockedAttacks(prev => prev + Math.floor(Math.random() * 10));
    };

    const interval = setInterval(updateStats, 2000);
    return () => clearInterval(interval);
  }, []);

  const threatPositions = [
    { x: 20, y: 30 },
    { x: 80, y: 20 },
    { x: 60, y: 70 },
    { x: 30, y: 80 },
    { x: 70, y: 60 },
  ];

  return (
    <RadarContainer>
      <GlobeContainer>
        <Globe
          animate={{
            rotateY: [0, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </GlobeContainer>

      <RadarRings>
        {[1, 2, 3].map((ring) => (
          <RadarRing
            key={ring}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: ring * 0.5,
            }}
          />
        ))}
        
        {threatPositions.map((pos, i) => (
          <ThreatDot
            key={i}
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3,
            }}
          />
        ))}
      </RadarRings>

      <RadarContent>
        <RadarTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          Global Threat Radar
        </RadarTitle>
        
        <RadarSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          viewport={{ once: true }}
        >
          Real-time monitoring of cyber threats across the digital landscape
        </RadarSubtitle>

        <ThreatStats>
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <StatNumber>{threatCount}</StatNumber>
            <StatLabel>Threats Detected</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <StatNumber>{activeThreats}</StatNumber>
            <StatLabel>Active Threats</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            viewport={{ once: true }}
          >
            <StatNumber>{blockedAttacks}</StatNumber>
            <StatLabel>Attacks Blocked</StatLabel>
          </StatItem>
        </ThreatStats>
      </RadarContent>
    </RadarContainer>
  );
};

export default ThreatRadar; 